import React, { createContext, useContext, useMemo } from 'react';
import qslib from 'query-string';
import { RouteComponentProps } from 'react-router';
import { PartialBy } from '../../../../../../../shared/types/typeUtils';
import { useApp } from './AppContext';

interface QueryStrings {
  date: string;
  lang: string;
  headless: boolean;
  event: string | undefined;
  offer: string | undefined;
  venue: string | undefined;
  eventTemplateId: string | undefined;
  offerTemplateId: string | undefined;
}

type PartialQueryStrings = PartialBy<QueryStrings, 'date' | 'lang' | 'headless'>;

interface QueryStringsContextProps {
  qs: QueryStrings;
  updateQs: <T extends keyof QueryStrings>(key: T, val: QueryStrings[T]) => void;
}

const DEFAULT_QUERY_STRINGS: PartialQueryStrings = {
  date: undefined,
  lang: undefined,
  headless: undefined,
  event: undefined,
  offer: undefined,
  venue: undefined,
  eventTemplateId: undefined,
  offerTemplateId: undefined,
};

const QueryStringsContext = createContext<QueryStringsContextProps | undefined>(undefined);

interface WithQueryStringsPropsBase {}

type WithQueryStringsProps = React.PropsWithChildren<
  WithQueryStringsPropsBase & RouteComponentProps
>;

export function WithQueryStrings(props: WithQueryStringsProps) {
  const { config } = useApp();

  const qs: PartialQueryStrings = useMemo(() => {
    const input = qslib.parse(props.location.search, { parseBooleans: true, parseNumbers: true });
    const result = { ...DEFAULT_QUERY_STRINGS };

    if (typeof input.date === 'string') result.date = input.date;
    if (typeof input.lang === 'string') result.lang = input.lang;
    if (typeof input.headless === 'boolean') result.headless = input.headless;
    if (typeof input.event === 'string') result.event = input.event;
    if (typeof input.offer === 'string') result.offer = input.offer;
    if (typeof input.venue === 'string') result.venue = input.venue;
    if (typeof input.eventTemplateId === 'string') result.eventTemplateId = input.eventTemplateId;
    if (typeof input.offerTemplateId === 'string') result.offerTemplateId = input.offerTemplateId;

    return result;
  }, [window.location.search]);

  function updateQs<T extends keyof QueryStrings>(key: T, val: QueryStrings[T]) {
    const newQs = { ...qs };

    if (['event', 'offer', 'venue'].includes(key)) {
      newQs.event = undefined;
      newQs.offer = undefined;
      newQs.venue = undefined;
      newQs.eventTemplateId = undefined;
      newQs.offerTemplateId = undefined;
    }

    newQs[key] = val;

    if (key === 'lang' && val === 'en') {
      newQs.lang = undefined;
    }

    if (key === 'date' && val === config.date) {
      newQs.date = undefined;
    }

    props.history.push({ search: qslib.stringify(newQs) });
  }

  const defaultedQs: QueryStrings = {
    ...qs,
    date: qs.date || config.date,
    lang: qs.lang || 'en',
    headless: !!qs.headless,
  };

  return (
    <QueryStringsContext.Provider value={{ qs: defaultedQs, updateQs }}>
      {props.children}
    </QueryStringsContext.Provider>
  );
}

export function useQueryStrings() {
  const context = useContext(QueryStringsContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
}
